/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Switch,
  Table,
  Row,
  Col,
  Tag,
  TimePicker
} from "antd";
import { FaRegEye, FaRedoAlt, FaPlus } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";
import {
  BsFillPauseBtnFill,
  BsFillPlayCircleFill,
  BsFillStopFill
} from "react-icons/bs";
import { format } from "date-fns";
import { _get, _post, _postnew, _put } from "../../../Utils/Helper";

interface QRCodeData {
  id: string;
  qrCode: string;
  user_type: string;
  expiry_datetime: string;
  selectedRules: string[];
  location?: string;
  status: string;
  locationIsBoolean: boolean;
  longitude?: number;
  latitude?: number;
}

const QRCodeExpiryTab = () => {
  const [expiryForm] = Form.useForm();
  const [ruleForm] = Form.useForm();
  const [qrCodes, setQrCodes] = useState<QRCodeData[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<QRCodeData | null>(null);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [longitude, setLongitude] = useState<number | undefined>();
  const [latitude, setLatitude] = useState<number | undefined>();

  const generateQRCodeBlob = async (data: string): Promise<Blob> => {
    try {
      const canvas = document.createElement("canvas");
      await QRCode.toCanvas(canvas, data, { width: 300 });
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to generate QR Code Blob"));
          }
        }, "image/png");
      });
    } catch (error) {
      console.error("Error generating QR code blob:", error);
      throw error;
    }
  };

  const handleGenerateQRWithExpiry = async (values: any) => {
    try {
      const expiryDate = new Date(values.expiry);
      if (!expiryDate || isNaN(expiryDate.getTime())) {
        throw new Error("Invalid expiry date");
      }

      // Format expiryDate to 'YYYY-MM-DD HH:mm:ss'
      const formattedExpiryDate = format(expiryDate, "yyyy-MM-dd HH:mm:ss");

      // Create FormData object
      const formData = new FormData();
      formData.append("query_type", "create");
      formData.append("user_type", values.user_type);
      formData.append("expiry_datetime", formattedExpiryDate); // Use formatted date
      formData.append("selectedRules", JSON.stringify(values.selectedRules));
      formData.append(
        "location",
        locationEnabled && longitude && latitude
          ? `${latitude}, ${longitude}`
          : "Not Required"
      );
      formData.append("location_is_boolean", locationEnabled ? "1" : "0"); // Convert boolean to integer
      formData.append("longitude", longitude?.toString() || "");
      formData.append("latitude", latitude?.toString() || "");
      formData.append("status", "active");

      // Generate QR Code Blob
      const qrCodeBlob = await generateQRCodeBlob(values.user_type);
      if (!qrCodeBlob) {
        throw new Error("Failed to generate QR code blob");
      }
      formData.append("qr_code", qrCodeBlob, "qrcode.png");

      // Log FormData for debugging
      console.log("FormData content:");
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      // Send the FormData via _postnew
      _postnew(
        "create-qrcode",
        formData,
        (response) => {
          console.log("New QR Code Response:", response); // Debugging success response
          if (response && response.id) {
            // Append the new QR code to the table
            setQrCodes((prev) => [...prev, response]);
          }
          message.success("QR Code generated and saved successfully");
          expiryForm.resetFields();
        },
        (error) => {
          console.error("Error generating QR code:", error);
          message.error("Failed to generate QR code");
        },
        false // Use FormData (not JSON)
      );
    } catch (error) {
      console.error(error);
      message.error(
        error instanceof Error ? error.message : "An error occurred"
      );
    }
  };


  useEffect(() => {
    _get(
      "rules",
      (data) => setRules(data),
      (error) => message.error("Failed to fetch rules")
    );
  }, []);

  const handleAddRule = (values: any) => {
    const newRule = {
      ...values,
      id: (rules.length + 1).toString(),
      status: "active"
    };
    setRules([...rules, newRule]);
    // _get('rules', newRule);
    _post("rules", newRule);
    message.success("Rule added successfully");
    setShowRuleModal(false);
    ruleForm.resetFields();
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          message.success("Location fetched successfully");
        },
        (error) => {
          message.error("Error fetching location: " + error.message);
        }
      );
    } else {
      message.error("Geolocation is not supported by this browser");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <Form
          form={expiryForm}
          layout="vertical"
          onFinish={handleGenerateQRWithExpiry}
        >
          <Row>
            <Col span={12}>
              <Button
                icon={<FaPlus />}
                onClick={() => setShowRuleModal(true)}
                type="default"
              >
                Add Rule
              </Button>
            </Col>
          </Row>
          <Row gutter={16} className="mb-4">
            <Col span={12}>
              <Form.Item
                name="user_type"
                label="User Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="teacher">Teacher</Select.Option>
                  <Select.Option value="staff">Staff</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="expiry"
                label="Expiry"
                rules={[{ required: true }]}
              >
                <DatePicker showTime />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="selectedRules"
            label="Select Rules"
            rules={[{ required: true }]}
          >
            <Select
              mode="multiple"
              placeholder="Select rules"
              options={rules.map((rule) => ({
                label: rule.rule_name,
                value: rule.id
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Enable Location"
            name="requireLocation"
            valuePropName="checked"
          >
            <Switch onChange={(checked) => setLocationEnabled(checked)} />
          </Form.Item>

          {locationEnabled && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Latitude">
                  <Input value={latitude ?? ""} disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Longitude">
                  <Input value={longitude ?? ""} disabled />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Button type="default" block onClick={getCurrentLocation}>
                  Fetch Current Location
                </Button>
              </Col>
            </Row>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Generate QR Code
            </Button>
          </Form.Item>
        </Form>
      </div>
      <Modal
        title="Add Rule"
        visible={showRuleModal}
        onCancel={() => setShowRuleModal(false)}
        footer={null}
      >
        <Form
          form={ruleForm}
          layout="vertical"
          onFinish={(values) => {
            // Extract only hours and minutes from TimePicker values
            const formattedValues = {
              ...values,
              startTime: values.startTime.format("HH:mm"), // Convert to HH:mm format
              endTime: values.endTime.format("HH:mm") // Convert to HH:mm format
            };
            handleAddRule(formattedValues); // Pass the transformed values to the handler
          }}
        >
          <Form.Item
            label="Rule Name"
            name="ruleName"
            rules={[{ required: true, message: "Please enter the rule name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Start Time"
            name="startTime"
            rules={[
              { required: true, message: "Please select the start time" }
            ]}
          >
            <TimePicker use12Hours format="h:mm a" className="w-100" />
          </Form.Item>
          <Form.Item
            label="End Time"
            name="endTime"
            rules={[{ required: true, message: "Please select the end time" }]}
          >
            <TimePicker use12Hours format="h:mm a" className="w-100" />
          </Form.Item>
          <Form.Item
            label="Location Required"
            name="locationIsBoolean"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" className="w-full">
              Add Rule
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        visible={showModal}
        title="QR Code Details"
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        {qrCodeData && (
          <div>
            <QRCodeSVG value={qrCodeData.qrCode} size={200} />
            <p>User Type: {qrCodeData.user_type}</p>
            <p>Expiry: {qrCodeData.expiry_datetime}</p>
            <p>Location: {qrCodeData.location}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QRCodeExpiryTab;
